import { del, get, getList, getPaginated, patch, post, postForm } from "./client";
import { ApiError, API_BASE } from "./client";
import { authHeader } from "./token";
import { setAccessToken } from "./token";
import type {
  AnalyticsRow,
  Branch,
  Dashboard,
  Member,
  MediaAsset,
  SellerListing,
  SellerProfile,
  OfficeExpense,
  OfficeExpenseCategory,
  ExpenseStats,
  ChatConversation,
  ChatMessage,
  AppNotification,
  DealerPaymentAccount,
  DealerLedgerEntry,
  DealerLedgerStats,
} from "./types";

/**
 * Typed wrappers over the §4.6 seller routes. One function per route, so a page
 * never assembles a URL by hand and a path change is a single edit.
 */

export async function login(
  identifier: string,
  password: string,
): Promise<{ accessToken: string }> {
  // The login field is `identifier`, not `email`: the API accepts a phone or an
  // email. The refresh token arrives as an httpOnly cookie we never read.
  const data = await post<{ accessToken: string }>("/auth/login", { identifier, password });
  return data;
}

/** Exchanges the httpOnly refresh cookie for a fresh access token. */
export async function refresh(): Promise<void> {
  const data = await post<{ accessToken: string }>("/auth/refresh");
  setAccessToken(data.accessToken);
}

export function logout(): Promise<unknown> {
  return post("/auth/logout");
}

// ── self-registration ────────────────────────────────────────────────────────

/**
 * AU-02. Creates the account as PENDING_VERIFICATION and issues a phone OTP.
 *
 * No access token comes back: the account cannot sign in until the OTP is verified
 * (AU-03), because `login` refuses an unverified contact. The panel therefore runs
 * register → verify → sign-in as one flow.
 */
export interface RegisterPhoneInput {
  phone: string;
  password: string;
  displayName: string;
  /** Optional; there is no later route to set it, so registration is the only chance. */
  email?: string;
}

export interface Account {
  id: string;
  phone: string | null;
  email: string | null;
  displayName: string;
  status: string;
}

export async function registerByPhone(input: RegisterPhoneInput): Promise<Account> {
  return post<Account>("/auth/register/phone", input);
}

/**
 * AU-03. Keyed by phone, never by an internal account id — an endpoint that
 * accepted an id from an unauthenticated caller would be an account oracle.
 *
 * A missing account, a wrong code and an expired code are one branch with one
 * message, so the response cannot enumerate registered numbers.
 */
export async function verifyPhone(phone: string, code: string): Promise<Account> {
  return post<Account>("/auth/verify/phone", { phone, code });
}

/** Always reports success, so it cannot be used to probe for registered numbers. */
export async function resendPhoneOtp(phone: string): Promise<{ sent: true }> {
  return post<{ sent: true }>("/auth/verify/phone/resend", { phone });
}

/**
 * Confirm an email address from the link in the verification mail.
 *
 * The API routes that link to this host rather than bikerhat.com whenever the
 * registration arrived from a partner-panel origin, so a partner is returned to
 * the panel rather than to the consumer site, which has no /verify-email route.
 */
export async function verifyEmailToken(token: string): Promise<Account> {
  return post<Account>("/auth/verify-email", { token });
}

/** Always reports success, registered or not. */
export async function requestPasswordReset(identifier: string): Promise<{ sent: true }> {
  return post<{ sent: true }>("/auth/password/forgot", { identifier });
}

/** `password`, not `newPassword` — the API's DTO field name. */
export async function resetPassword(token: string, password: string): Promise<unknown> {
  return post<unknown>("/auth/password/reset", { token, password });
}

export function fetchMe(): Promise<SellerProfile> {
  return get<SellerProfile>("/sellers/me");
}

export function createSeller(body: {
  displayName: string;
  sellerType?: SellerProfile["sellerType"];
  businessName?: string;
}): Promise<SellerProfile> {
  return post<SellerProfile>("/sellers", body);
}

export function updateSeller(patchBody: Partial<SellerProfile>): Promise<SellerProfile> {
  return patch<SellerProfile>("/sellers/me", patchBody);
}

export function fetchDashboard(): Promise<Dashboard> {
  return get<Dashboard>("/sellers/me/dashboard");
}

export function fetchAnalytics(page = 1, limit = 20) {
  return getPaginated<AnalyticsRow>(`/sellers/me/analytics?page=${page}&limit=${limit}`);
}

export function fetchSellerListings(page = 1, limit = 20, status?: string) {
  const qs = new URLSearchParams();
  qs.set("page", String(page));
  qs.set("limit", String(limit));
  if (status) qs.set("status", status);
  return getPaginated<SellerListing>(`/sellers/me/listings?${qs.toString()}`);
}

// ── sales (dealer sale book, S4/S5) ──────────────────────────────────────────

export interface SaleRow {
  id: string;
  sellerId: string;
  invoiceNumber: string;
  invoiceNo: string;
  status: string;
  paymentStatus: string;
  saleDate: string;
  createdAt: string;
  updatedAt: string;
  currency: string;
  bikePrice: number;
  discountPercent: number;
  discountAmount: number;
  chargesTotal: number;
  totalAmount: number;
  paidAmount: number;
  dueAmount: number;
  listing: { id: string; title: string; publicId: string; modelYear: number | null } | null;
  customer: { id: string; name: string; phone: string; email: string | null };
  invoice: { id: string; status: string; version: number } | null;
  pricingBreakdown: SaleChargeDetail[];
}

export interface SaleChargeInput {
  kind: string;
  label: string;
  amount: number;
  sortOrder?: number;
  ratePercent?: number;
  baseAmount?: number;
  type?: "POSITIVE" | "NEGATIVE";
  costAmount?: number;
}

export interface SaleChargeDetail {
  kind: string;
  label: string;
  amount: number;
  type: "POSITIVE" | "NEGATIVE";
  ratePercent: number | null;
  baseAmount: number | null;
  costAmount: number | null;
  sortOrder: number;
}

export interface CreateSaleInput {
  listingId: string;
  customerId?: string;
  customerName?: string;
  customerPhone?: string;
  bikePrice: number;
  discountPercent?: number;
  discountAmount?: number;
  charges?: SaleChargeInput[];
  paymentAccountId?: string;
  firstPaymentAmount?: number;
  transactionReference?: string;
  saleDate?: string;
  notes?: string;
}

export function fetchSales(
  page = 1,
  limit = 20,
  filters?: { status?: string; paymentStatus?: string; q?: string; customerId?: string },
) {
  const qs = new URLSearchParams();
  qs.set("page", String(page));
  qs.set("limit", String(limit));
  if (filters?.status) qs.set("status", filters.status);
  if (filters?.paymentStatus) qs.set("paymentStatus", filters.paymentStatus);
  if (filters?.q) qs.set("q", filters.q);
  if (filters?.customerId) qs.set("customerId", filters.customerId);
  return getPaginated<SaleRow>(`/sales?${qs.toString()}`);
}

export function fetchSale(id: string): Promise<SaleRow> {
  return get<SaleRow>(`/sales/${encodeURIComponent(id)}`);
}

export function createSale(input: CreateSaleInput): Promise<SaleRow> {
  return post<SaleRow>("/sales", input);
}

export function updateSale(id: string, input: Partial<CreateSaleInput>): Promise<SaleRow> {
  return patch<SaleRow>(`/sales/${encodeURIComponent(id)}`, input);
}

export function voidSale(id: string, reason: string): Promise<{ success: boolean }> {
  return post<{ success: boolean }>(`/sales/${encodeURIComponent(id)}/void`, { reason });
}

export interface SalesStats {
  count: number;
  totalRevenue: number;
  totalCollected: number;
  totalDue: number;
  byPaymentStatus: { paymentStatus: string; count: number; total: number }[];
}

export function fetchSalesStats(): Promise<SalesStats> {
  return get<SalesStats>("/sales/stats");
}

export interface SalePaymentRow {
  id: string;
  amount: number;
  paidAt: string;
  method: string;
  reference: string | null;
  receiptUrl: string | null;
  notes: string | null;
  voidedAt: string | null;
  voidReason: string | null;
  paymentAccount?: { id: string; name: string; accountType: string } | null;
}

export function fetchSalePayments(saleId: string): Promise<SalePaymentRow[]> {
  return get<SalePaymentRow[]>(`/sales/${encodeURIComponent(saleId)}/payments`);
}

export function createSalePayment(
  saleId: string,
  input: { amount: number; paymentAccountId?: string; method?: string; reference?: string; notes?: string; paidAt?: string },
): Promise<SaleRow> {
  return post<SaleRow>(`/sales/${encodeURIComponent(saleId)}/payments`, input);
}

export function voidSalePayment(saleId: string, paymentId: string, reason?: string): Promise<SaleRow> {
  return post<SaleRow>(`/sales/${encodeURIComponent(saleId)}/payments/${encodeURIComponent(paymentId)}/void`, {
    reason,
  });
}

export function fetchMembers(): Promise<Member[]> {
  return getList<Member>("/sellers/me/members");
}

export function addMember(accountId: string, role: Member["role"]): Promise<Member> {
  return post<Member>("/sellers/me/members", { accountId, role });
}

export function removeMember(accountId: string): Promise<unknown> {
  return del(`/sellers/me/members/${encodeURIComponent(accountId)}`);
}

export function fetchBranches(): Promise<Branch[]> {
  return getList<Branch>("/sellers/me/branches");
}

export function createBranch(body: {
  name: string;
  addressLine?: string;
  cityId?: string;
  latitude?: number;
  longitude?: number;
  isPublic?: boolean;
}): Promise<Branch> {
  return post<Branch>("/sellers/me/branches", body);
}

export function updateBranch(id: string, body: Partial<Branch>): Promise<Branch> {
  return patch<Branch>(`/sellers/me/branches/${id}`, body);
}

export function deleteBranch(id: string): Promise<unknown> {
  return del(`/sellers/me/branches/${id}`);
}

export function pauseSeller(): Promise<{ paused: boolean; listingsPaused: number }> {
  return post("/sellers/me/pause");
}

export function resumeSeller(): Promise<{ resumed: boolean; listingsResumed: number }> {
  return post("/sellers/me/resume");
}

// ── listings (bike management) ──────────────────────────────────────────────

export interface ListingSpecInput {
  powertrain: "PETROL" | "ELECTRIC" | "HYBRID";
  transmission?: "MANUAL" | "AUTOMATIC" | "DCT";
  engineCc?: number;
  mileageKmpl?: number;
  odometerKm?: number;
  motorPowerKw?: number;
  batteryCapacityKwh?: number;
  batteryRangeKm?: number;
  fuelSystem?: string;
  color?: string;
}

export interface DisclosureInput {
  field:
    | "REPAIRS"
    | "MODIFICATIONS"
    | "ACCIDENT_HISTORY"
    | "SERVICE_HISTORY"
    | "PREVIOUS_OWNERS"
    | "WARRANTY"
    | "MODIFICATIONS_PENDING";
  status: "VALID" | "EXPIRED" | "MISSING" | "UNKNOWN";
  notes?: string;
}

export interface DocumentInput {
  registrationNumber?: string;
  issuedAt?: string;
  expiresAt?: string;
  declaredStatus: "VALID" | "EXPIRED" | "MISSING" | "UNKNOWN";
  notes?: string;
}

export type ListingTypeValue = "INDIVIDUAL_SALE" | "DEALER_INVENTORY" | "SHOWROOM_OFFER";
export type ConditionValue =
  | "NEW"
  | "USED"
  | "REFURBISHED"
  | "DAMAGED_FOR_PARTS"
  | "RECONDITIONED_IMPORTED";
export type ContactPreferenceValue = "CHAT_ONLY" | "CHAT_AND_CALL";
export type ListingStatusValue =
  | "DRAFT"
  | "PENDING_REVIEW"
  | "PUBLISHED"
  | "REJECTED"
  | "PAUSED"
  | "SOLD"
  | "EXPIRED"
  | "REMOVED";

export interface CreateListingInput {
  title: string;
  description: string;
  listingType: ListingTypeValue;
  condition: ConditionValue;
  brandId: string;
  modelId?: string;
  customModelName?: string;
  price: number;
  currency?: string;
  negotiable?: boolean;
  modelYear?: number;
  manufacturingYear?: number;
  cityId: string;
  areaText?: string;
  latitude?: number;
  longitude?: number;
  contactPreference: ContactPreferenceValue;
  allowVideoCall?: boolean;
  spec?: ListingSpecInput;
  disclosures?: DisclosureInput[];
  documents?: DocumentInput[];
}

export interface Listing {
  id: string;
  publicId: string;
  slug: string;
  sellerId: string;
  brandId: string;
  modelId: string | null;
  customModelName: string | null;
  listingType: ListingTypeValue;
  condition: ConditionValue;
  status: ListingStatusValue;
  title: string;
  description: string;
  price: number;
  currency: string;
  negotiable: boolean;
  modelYear: number | null;
  manufacturingYear: number | null;
  countryId?: string;
  cityId?: string;
  location?: {
    countryId: string;
    cityId: string | null;
    areaText: string | null;
    latitude: number | null;
    longitude: number | null;
  };
  areaText: string | null;
  latitude: number | null;
  longitude: number | null;
  contactPreference: ContactPreferenceValue;
  allowVideoCall: boolean;
  availabilityConfirmedAt: string | null;
  availabilityExpiresAt: string | null;
  viewCount: number;
  photoCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ListingPhoto {
  id: string;
  listingId: string;
  assetId: string;
  position: number;
  isCover: boolean;
  width: number | null;
  height: number | null;
  byteSize: number | null;
  asset: MediaAsset;
}

export interface ListingReadiness {
  score: number;
  canPublish: boolean;
  blocking: string[];
  warnings: string[];
  checks: Record<string, unknown>;
}

export function fetchListing(id: string): Promise<Listing> {
  return get<Listing>(`/me/listings/${encodeURIComponent(id)}`);
}

export function createListing(body: CreateListingInput): Promise<Listing> {
  return post<Listing>("/me/listings", body);
}

export function updateListing(id: string, body: Partial<CreateListingInput>): Promise<Listing> {
  return patch<Listing>(`/me/listings/${encodeURIComponent(id)}`, body);
}

export function deleteListing(id: string): Promise<unknown> {
  return del(`/me/listings/${encodeURIComponent(id)}`);
}

export function listingReadiness(id: string): Promise<ListingReadiness> {
  return get<ListingReadiness>(`/me/listings/${encodeURIComponent(id)}/readiness`);
}

export function listingPublish(id: string): Promise<unknown> {
  return post(`/me/listings/${encodeURIComponent(id)}/publish`);
}

export function listingPause(id: string): Promise<unknown> {
  return post(`/me/listings/${encodeURIComponent(id)}/pause`);
}

export function listingResume(id: string): Promise<unknown> {
  return post(`/me/listings/${encodeURIComponent(id)}/resume`);
}

export function listingSold(id: string): Promise<unknown> {
  return post(`/me/listings/${encodeURIComponent(id)}/sold`);
}

export function listingRelist(id: string): Promise<unknown> {
  return post(`/me/listings/${encodeURIComponent(id)}/relist`);
}

export interface AddPhotoInput {
  assetId: string;
  position?: number;
  isCover?: boolean;
}

export function addListingPhoto(id: string, body: AddPhotoInput): Promise<ListingPhoto> {
  return post<ListingPhoto>(`/me/listings/${encodeURIComponent(id)}/photos`, body);
}

export function reorderListingPhotos(id: string, photoIds: string[]): Promise<unknown> {
  return patch(`/me/listings/${encodeURIComponent(id)}/photos/order`, { photoIds });
}

export function deleteListingPhoto(id: string, photoId: string): Promise<unknown> {
  return del(`/me/listings/${encodeURIComponent(id)}/photos/${encodeURIComponent(photoId)}`);
}

// ── media ───────────────────────────────────────────────────────────────────

export function uploadMedia(file: File): Promise<MediaAsset> {
  const formData = new FormData();
  formData.append("file", file);
  return postForm<MediaAsset>("/media/upload", formData).then((r) => r.data);
}

export function deleteMedia(id: string): Promise<unknown> {
  return del(`/media/${encodeURIComponent(id)}`);
}

// ── catalog (public taxonomy for the Add Bike form) ─────────────────────────

export interface Brand {
  id: string;
  slug: string;
  name: string;
  modelCount?: number;
}

/**
 * The catalog payload is `ModelPresentation` from the API, not just an id/name.
 * Add Bike reads the spec columns to fill engine/type/year when a model is
 * picked, so the type must carry them or the form silently loses the data.
 */
export interface Model {
  id: string;
  slug: string;
  name: string;
  /** Present on the public catalog payload; used to keep a stale model id from
   *  being submitted after the brand changes. */
  brand?: { id: string; name: string; slug: string } | null;
  /** BodyCategory — COMMUTER | SPORT | SCOOTER | ELECTRIC_SCOOTER | … */
  category: string;
  yearFrom: number | null;
  yearTo: number | null;
  engineCc: number | null;
  motorPowerKw: number | null;
  batteryCapacityKwh: number | null;
  maxPowerBhp: number | null;
  maxTorqueNm: number | null;
  mileageKmpl: number | null;
  brakeSystem: string | null;
  gearbox: string | null;
  seatHeightMm: number | null;
  weightKg: number | null;
}

export function fetchBrands(): Promise<Brand[]> {
  // limit=100 is MAX_PAGE_SIZE. The default is 20, and Hero alone has 38
  // models now — a truncated list looks like a missing model.
  return getList<Brand>("/brands?limit=100");
}

export function fetchBrandModels(brandSlug: string): Promise<Model[]> {
  return getList<Model>(`/brands/${encodeURIComponent(brandSlug)}/models?limit=100`);
}

export interface City {
  id: string;
  countryId: string;
  name: string;
  nameBn: string | null;
  slug: string;
  latitude: number | null;
  longitude: number | null;
  isActive: boolean;
  sortOrder: number;
}

export function fetchCities(country = "BD"): Promise<City[]> {
  return get<City[]>(`/cities?country=${encodeURIComponent(country)}`);
}

// ── listings wizard (guided create) ────────────────────────────────────────────

export interface AutoTitleRequest {
  brandId: string;
  modelId?: string;
  customModelName?: string;
  modelYear?: number;
  condition: ConditionValue;
  listingType: ListingTypeValue;
}

export interface AiDescriptionRequest {
  brandId: string;
  modelId?: string;
  customModelName?: string;
  modelYear?: number;
  condition: ConditionValue;
  listingType: ListingTypeValue;
  spec?: ListingSpecInput;
  disclosures?: DisclosureInput[];
  price?: number;
  areaText?: string;
}

export interface WizardBrandsModelsResponse {
  brands: Array<{
    id: string;
    name: string;
    slug: string;
    logoAssetId?: string;
    modelCount: number;
  }>;
  models: Array<{
    id: string;
    brandId: string;
    name: string;
    slug: string;
    category: string;
    yearFrom?: number;
    yearTo?: number;
    engineCc?: number;
    motorPowerKw?: number;
    batteryCapacityKwh?: number;
    officialPrice?: number;
    officialPriceCurrency?: string;
    seatHeightMm?: number;
    has3DAsset: boolean;
  }>;
}

export interface AutoTitleResponse {
  title: string;
}

export interface AiDescriptionResponse {
  description: string;
}

export function fetchWizardBrandsModels(): Promise<WizardBrandsModelsResponse> {
  return get<WizardBrandsModelsResponse>("/me/listings/wizard/brands-models");
}

export function fetchWizardModelsByBrand(brandId: string): Promise<WizardBrandsModelsResponse> {
  return get<WizardBrandsModelsResponse>(`/me/listings/wizard/brands/${encodeURIComponent(brandId)}/models`);
}

export function generateAutoTitle(body: AutoTitleRequest): Promise<AutoTitleResponse> {
  return post<AutoTitleResponse>("/me/listings/wizard/auto-title", body);
}

export function generateAiDescription(body: AiDescriptionRequest): Promise<AiDescriptionResponse> {
  return post<AiDescriptionResponse>("/me/listings/wizard/ai-description", body);
}

export interface WizardDraftCreateInput {
  step1: {
    listingType: ListingTypeValue;
    condition: ConditionValue;
    brandId: string;
    modelId?: string;
    customModelName?: string;
    modelYear?: number;
  };
  step2?: {
    spec?: ListingSpecInput;
  };
  step3?: {
    assetIds: string[];
    coverIndex?: number;
  };
  step4: {
    price: number;
    currency?: string;
    negotiable?: boolean;
    cityId: string;
    areaText?: string;
    contactPreference: ContactPreferenceValue;
    allowVideoCall?: boolean;
  };
  step5?: {
    title?: string;
    description?: string;
    disclosures?: DisclosureInput[];
    documents?: DocumentInput[];
  };
}

export function createListingFromWizard(body: WizardDraftCreateInput): Promise<Listing> {
  return post<Listing>("/me/listings/wizard/create-from-steps", body);
}

export function checkListingReadiness(id: string): Promise<ListingReadiness> {
  return get<ListingReadiness>(`/me/listings/wizard/${encodeURIComponent(id)}/readiness`);
}

export function publishListingWizard(id: string): Promise<unknown> {
  return post(`/me/listings/wizard/${encodeURIComponent(id)}/publish`);
}

// ── office expenses ──────────────────────────────────────────────────────────

export interface CreateOfficeExpenseInput {
  title: string;
  amount: number;
  currency?: string;
  categoryId?: string;
  branchId?: string;
  paymentMethod?: string;
  paymentAccount?: string;
  paymentAccountId?: string;
  expenseDate?: string;
  reference?: string;
  receiptUrl?: string;
  notes?: string;
}

export function fetchOfficeExpenses(filters?: {
  fromDate?: string;
  toDate?: string;
  categoryId?: string;
  branchId?: string;
  search?: string;
  page?: number;
  limit?: number;
}): Promise<{ rows: OfficeExpense[]; total: number; page: number; totalPages: number }> {
  const qs = new URLSearchParams();
  if (filters?.fromDate) qs.set("fromDate", filters.fromDate);
  if (filters?.toDate) qs.set("toDate", filters.toDate);
  if (filters?.categoryId) qs.set("categoryId", filters.categoryId);
  if (filters?.branchId) qs.set("branchId", filters.branchId);
  if (filters?.search) qs.set("search", filters.search);
  if (filters?.page) qs.set("page", String(filters.page));
  if (filters?.limit) qs.set("limit", String(filters.limit));
  const query = qs.toString();
  return getPaginated<OfficeExpense>(`/office-expenses${query ? `?${query}` : ""}`);
}

export function fetchExpenseStats(branchId?: string): Promise<ExpenseStats> {
  const query = branchId ? `?branchId=${encodeURIComponent(branchId)}` : "";
  return get<ExpenseStats>(`/office-expenses/stats${query}`);
}

export function fetchOfficeExpense(id: string): Promise<OfficeExpense> {
  return get<OfficeExpense>(`/office-expenses/${encodeURIComponent(id)}`);
}

export function createOfficeExpense(input: CreateOfficeExpenseInput): Promise<OfficeExpense> {
  return post<OfficeExpense>("/office-expenses", input);
}

export function updateOfficeExpense(
  id: string,
  input: Partial<CreateOfficeExpenseInput>,
): Promise<OfficeExpense> {
  return patch<OfficeExpense>(`/office-expenses/${encodeURIComponent(id)}`, input);
}

export function deleteOfficeExpense(id: string): Promise<unknown> {
  return del(`/office-expenses/${encodeURIComponent(id)}`);
}

// ── office expense categories ────────────────────────────────────────────────

export function fetchExpenseCategories(): Promise<OfficeExpenseCategory[]> {
  return getList<OfficeExpenseCategory>("/expense-categories");
}

export function createExpenseCategory(input: {
  name: string;
  description?: string;
}): Promise<OfficeExpenseCategory> {
  return post<OfficeExpenseCategory>("/expense-categories", input);
}

export function updateExpenseCategory(
  id: string,
  input: { name?: string; description?: string },
): Promise<OfficeExpenseCategory> {
  return patch<OfficeExpenseCategory>(`/expense-categories/${encodeURIComponent(id)}`, input);
}

export function deleteExpenseCategory(id: string): Promise<unknown> {
  return del(`/expense-categories/${encodeURIComponent(id)}`);
}

// ── live chat ──────────────────────────────────────────────────────────────

/** The chat list endpoint returns either a bare array or `{ data }`/`{ rows }`. */
export type ChatConversationList = ChatConversation[] | { data?: ChatConversation[]; rows?: ChatConversation[] };
/** Messages likewise, so the page can normalise without `any`. */
export type ChatMessageList = ChatMessage[] | { data?: ChatMessage[]; rows?: ChatMessage[] };

export function fetchChatConversations(
  page = 1,
  limit = 30,
): Promise<ChatConversationList> {
  return get<ChatConversationList>(
    `/chat/conversations?page=${page}&limit=${limit}`,
  );
}

export function fetchChatConversation(id: string): Promise<ChatConversation> {
  return get<ChatConversation>(`/chat/conversations/${encodeURIComponent(id)}`);
}

export function fetchConversationMessages(
  conversationId: string,
  cursor?: string,
  limit = 50,
): Promise<ChatMessageList> {
  const query = cursor
    ? `?cursor=${encodeURIComponent(cursor)}&limit=${limit}`
    : `?limit=${limit}`;
  return get<ChatMessageList>(
    `/chat/conversations/${encodeURIComponent(conversationId)}/messages${query}`,
  );
}

export function sendChatMessage(
  conversationId: string,
  body: string,
  mediaUrl?: string,
): Promise<ChatMessage> {
  return post<ChatMessage>(`/chat/conversations/${encodeURIComponent(conversationId)}/messages`, {
    content: body,
    mediaUrl,
  });
}

export function markConversationRead(
  conversationId: string,
): Promise<{ success: boolean; count: number }> {
  return patch<{ success: boolean; count: number }>(
    `/chat/conversations/${encodeURIComponent(conversationId)}/read`,
    {},
  );
}

export function fetchChatUnreadCount(): Promise<{ total: number; unreadCount?: number }> {
  return get<{ total: number; unreadCount?: number }>("/chat/conversations/unread-count");
}

// ── live notifications ──────────────────────────────────────────────────────

export function fetchNotifications(
  page = 1,
  limit = 20,
  unreadOnly = false,
): Promise<{ data: AppNotification[]; total: number; page: number; totalPages: number; unreadCount: number }> {
  const query = `?page=${page}&limit=${limit}${unreadOnly ? "&unreadOnly=true" : ""}`;
  return get<{ data: AppNotification[]; total: number; page: number; totalPages: number; unreadCount: number }>(
    `/notifications${query}`,
  );
}

export function markNotificationRead(id: string): Promise<{ success: boolean }> {
  return patch<{ success: boolean }>(`/notifications/${encodeURIComponent(id)}/read`, {});
}

export function markAllNotificationsRead(): Promise<{ success: boolean; count: number }> {
  return patch<{ success: boolean; count: number }>("/notifications/read-all", {});
}

export function fetchNotificationUnreadCount(): Promise<{ unreadCount: number }> {
  return get<{ unreadCount: number }>("/notifications/unread-count");
}

// ── dealer finance & ledger ────────────────────────────────────────────────

export interface CreatePaymentAccountInput {
  name: string;
  accountType: string;
  accountNumber?: string;
  bankName?: string;
  branchName?: string;
  routingNumber?: string;
  initialBalance?: number;
  isDefault?: boolean;
}

export interface AdjustBalanceInput {
  amount: number;
  adjustmentType: "DEPOSIT" | "WITHDRAWAL" | "ADJUSTMENT";
  title: string;
  description?: string;
}

export interface RecordDealerSaleInput {
  listingId: string;
  salePrice: number;
  paymentAccountId: string;
  buyerName?: string;
  buyerPhone?: string;
  transactionReference?: string;
  notes?: string;
}

export function fetchDealerPaymentAccounts(includeInactive = false): Promise<DealerPaymentAccount[]> {
  return getList<DealerPaymentAccount>(`/dealer-finance/accounts${includeInactive ? "?includeInactive=true" : ""}`);
}

export function createDealerPaymentAccount(input: CreatePaymentAccountInput): Promise<DealerPaymentAccount> {
  return post<DealerPaymentAccount>("/dealer-finance/accounts", input);
}

export function updateDealerPaymentAccount(
  id: string,
  input: Partial<CreatePaymentAccountInput & { isActive?: boolean }>,
): Promise<DealerPaymentAccount> {
  return patch<DealerPaymentAccount>(`/dealer-finance/accounts/${encodeURIComponent(id)}`, input);
}

export function deleteDealerPaymentAccount(id: string): Promise<{ success: boolean; message: string }> {
  return del<{ success: boolean; message: string }>(`/dealer-finance/accounts/${encodeURIComponent(id)}`);
}

export function adjustDealerAccountBalance(
  accountId: string,
  input: AdjustBalanceInput,
): Promise<{ account: DealerPaymentAccount; ledgerEntry: DealerLedgerEntry }> {
  return post<{ account: DealerPaymentAccount; ledgerEntry: DealerLedgerEntry }>(
    `/dealer-finance/accounts/${encodeURIComponent(accountId)}/adjust`,
    input,
  );
}

export function recordDealerSale(
  input: RecordDealerSaleInput,
): Promise<{ success: boolean; message: string; data: { listing: unknown; account: DealerPaymentAccount; ledgerEntry: DealerLedgerEntry } }> {
  return post<{ success: boolean; message: string; data: { listing: unknown; account: DealerPaymentAccount; ledgerEntry: DealerLedgerEntry } }>(
    "/dealer-finance/record-sale",
    input,
  );
}

export function fetchDealerLedger(params?: {
  page?: number;
  limit?: number;
  paymentAccountId?: string;
  entryType?: string;
  source?: string;
  fromDate?: string;
  toDate?: string;
}): Promise<{ items: DealerLedgerEntry[]; total: number; page: number; totalPages: number }> {
  const qs = new URLSearchParams();
  if (params?.page) qs.set("page", String(params.page));
  if (params?.limit) qs.set("limit", String(params.limit));
  if (params?.paymentAccountId) qs.set("paymentAccountId", params.paymentAccountId);
  if (params?.entryType) qs.set("entryType", params.entryType);
  if (params?.source) qs.set("source", params.source);
  if (params?.fromDate) qs.set("fromDate", params.fromDate);
  if (params?.toDate) qs.set("toDate", params.toDate);
  const query = qs.toString();
  return get<{ items: DealerLedgerEntry[]; total: number; page: number; totalPages: number }>(
    `/dealer-finance/ledger${query ? `?${query}` : ""}`,
  );
}

export function fetchDealerLedgerStats(params?: {
  paymentAccountId?: string;
  fromDate?: string;
  toDate?: string;
}): Promise<DealerLedgerStats> {
  const qs = new URLSearchParams();
  if (params?.paymentAccountId) qs.set("paymentAccountId", params.paymentAccountId);
  if (params?.fromDate) qs.set("fromDate", params.fromDate);
  if (params?.toDate) qs.set("toDate", params.toDate);
  const query = qs.toString();
  return get<DealerLedgerStats>(`/dealer-finance/ledger/stats${query ? `?${query}` : ""}`);
}


/**
 * ── sale invoice endpoints ────────────────────────────────────────────────
 */

export interface SaleInvoiceRow {
  id: string;
  invoiceNo: string;
  vehicleTitle: string;
  vehiclePublicId: string;
  buyerName: string;
  buyerEmail: string;
  saleDate: string;
  total: number;
  paid: number;
  balance: number;
  status: "PAID" | "PENDING" | "OVERDUE";
}

export async function fetchSaleInvoices(
  page = 1,
  limit = 20,
): Promise<{ rows: SaleInvoiceRow[]; total: number; page: number; totalPages: number }> {
  const result = await fetchSales(page, limit);
  return {
    rows: result.rows.map((s): SaleInvoiceRow => ({
      id: s.id,
      invoiceNo: s.invoiceNo,
      vehicleTitle: s.listing?.title ?? "Vehicle",
      vehiclePublicId: s.listing?.publicId ?? "",
      buyerName: s.customer.name,
      buyerEmail: s.customer.email ?? "",
      saleDate: s.saleDate?.slice(0, 10) ?? "",
      total: s.totalAmount,
      paid: s.paidAmount,
      balance: s.dueAmount,
      status: s.dueAmount <= 0 ? "PAID" : "PENDING",
    })),
    total: result.total,
    page: result.page,
    totalPages: result.totalPages,
  };
}

// ── invoice settings ────────────────────────────────────────────────────────
export interface InvoiceSettingsSalesDefaults {
  defaultDiscountPercent?: number;
  defaultCharges?: SaleChargeInput[];
  defaultPaymentAccountId?: string;
  defaultNotes?: string;
}

export interface InvoiceSettings {
  businessName: string;
  businessAddress: string;
  phoneNumber: string;
  email: string;
  logoUrl: string;
  primaryColor: string;
  fontFamily: string;
  termsAndConditions: string;
  website: string;
  facebook: string;
  twitter: string;
  youtube: string;
  tiktok: string;
  whatsapp: string;
  templateId?: string;
  salesDefaults?: InvoiceSettingsSalesDefaults | null;
}

export async function fetchInvoiceSettings(): Promise<InvoiceSettings> {
  return get<InvoiceSettings>("/invoice/settings");
}

export async function updateInvoiceSettings(body: Partial<InvoiceSettings>): Promise<InvoiceSettings> {
  return patch<InvoiceSettings>("/invoice/settings", body);
}

export interface InvoiceTemplate {
  id: string;
  name: string;
  description?: string;
}

export async function fetchInvoiceTemplates(): Promise<InvoiceTemplate[]> {
  return get<InvoiceTemplate[]>("/invoice/templates");
}

export async function downloadInvoicePdf(invoiceId: string): Promise<Blob> {
  const bearer = authHeader();
  const url = `${API_BASE}/api/v1/sales/${encodeURIComponent(invoiceId)}/invoice/pdf`;

  const res = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers: {
      ...((bearer as { Authorization?: string }) ?? {}),
      "Content-Type": "application/json",
    },
  });

  if (!res.ok) {
    const text = await res.text();
    let json: unknown;
    try {
      json = JSON.parse(text);
    } catch {
      json = { message: text.slice(0, 200) };
    }
    const body = json as { message?: string } | null;
    throw new ApiError(res.status, body?.message ?? `Download failed (${res.status})`);
  }

  // Handle binary response
  return res.blob();
}

export async function sendInvoiceEmail(invoiceId: string): Promise<{ success: boolean }> {
  return post<{ success: boolean }>(`/sales/${encodeURIComponent(invoiceId)}/invoice/resend`, {});
}



